import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, Button, Form, Alert, Badge, Row, Col, Table, Spinner } from 'react-bootstrap';
import { Envelope, CheckCircle, XCircle, Send, Gear } from 'react-bootstrap-icons';

const apiUrl = "/api";

function EmailSettings({ tenantId }) {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [alert, setAlert] = useState(null);
    const [emailLog, setEmailLog] = useState([]);

    const [formData, setFormData] = useState({
        provider: 'outlook',
        client_id: '',
        client_secret: '',
        email_address: '',
        display_name: '',
        notify_low_stock: true,
        notify_frequency: 'daily',
        notify_recipients: '',
        low_stock_threshold_override: ''
    });

    useEffect(() => {
        loadConfig();
        loadEmailLog();
    }, [tenantId]);

    const loadConfig = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.get(`${apiUrl}/admin/email-config?tenant_id=${tenantId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.configured) {
                setConfig(res.data.config);
                setFormData({
                    provider: res.data.config.provider || 'outlook',
                    client_id: '', // Don't show existing credentials
                    client_secret: '',
                    email_address: res.data.config.email_address || '',
                    display_name: res.data.config.display_name || '',
                    notify_low_stock: res.data.config.notify_low_stock !== false,
                    notify_frequency: res.data.config.notify_frequency || 'daily',
                    notify_recipients: res.data.config.notify_recipients || '',
                    low_stock_threshold_override: res.data.config.low_stock_threshold_override || ''
                });
            }
        } catch (error) {
            console.error('Error loading email config:', error);
            setAlert({ type: 'danger', message: 'Failed to load email configuration' });
        } finally {
            setLoading(false);
        }
    };

    const loadEmailLog = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${apiUrl}/admin/email-log?tenant_id=${tenantId}&limit=10`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEmailLog(res.data);
        } catch (error) {
            console.error('Error loading email log:', error);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const token = localStorage.getItem('token');
            await axios.post(`${apiUrl}/admin/email-config`, { ...formData, tenant_id: tenantId }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAlert({ type: 'success', message: 'Email configuration saved successfully' });
            loadConfig();
        } catch (error) {
            setAlert({ type: 'danger', message: error.response?.data?.error || 'Error saving configuration' });
        } finally {
            setSaving(false);
        }
    };

    const handleActivate = async (activate) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${apiUrl}/admin/email-config/activate`, {
                is_active: activate,
                tenant_id: tenantId
            }, { headers: { Authorization: `Bearer ${token}` } });
            setAlert({ type: 'success', message: `Email notifications ${activate ? 'activated' : 'deactivated'}` });
            loadConfig();
        } catch (error) {
            setAlert({ type: 'danger', message: 'Error updating status' });
        }
    };

    const handleTest = async () => {
        try {
            setTesting(true);
            const token = localStorage.getItem('token');
            const res = await axios.post(`${apiUrl}/admin/email-config/test`, {
                tenant_id: tenantId,
                recipient: formData.notify_recipients
            }, { headers: { Authorization: `Bearer ${token}` } });
            setAlert({ type: 'success', message: res.data.message });
            loadEmailLog();
        } catch (error) {
            setAlert({ type: 'danger', message: error.response?.data?.error || 'Error sending test email' });
        } finally {
            setTesting(false);
        }
    };

    const handleSendLowStockAlert = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${apiUrl}/admin/send-low-stock-alert`, { tenant_id: tenantId }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAlert({ type: 'success', message: res.data.message });
            loadEmailLog();
        } catch (error) {
            setAlert({ type: 'danger', message: error.response?.data?.error || 'Error sending alert' });
        }
    };

    if (loading) {
        return <div className="text-center py-4"><Spinner animation="border" /></div>;
    }

    return (
        <div>
            {alert && <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>{alert.message}</Alert>}

            {/* Status Card */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0"><Envelope className="me-2" />Email Notification Status</h5>
                </Card.Header>
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={6}>
                            <div className="d-flex align-items-center">
                                {config?.is_active ? (
                                    <Badge bg="success" className="fs-6 me-3"><CheckCircle className="me-1" />Active</Badge>
                                ) : (
                                    <Badge bg="secondary" className="fs-6 me-3"><XCircle className="me-1" />Inactive</Badge>
                                )}
                                {config?.email_address && <span className="text-muted">{config.email_address}</span>}
                            </div>
                        </Col>
                        <Col md={6} className="text-md-end mt-3 mt-md-0">
                            {config?.is_active ? (
                                <Button variant="outline-danger" onClick={() => handleActivate(false)}>Deactivate</Button>
                            ) : config ? (
                                <Button variant="success" onClick={() => handleActivate(true)}>Activate Notifications</Button>
                            ) : null}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Configuration Form */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0"><Gear className="me-2" />Email Configuration (OAuth)</h5>
                </Card.Header>
                <Card.Body>
                    <Alert variant="info">
                        <strong>Microsoft Outlook OAuth Setup:</strong>
                        <ol className="mb-0 mt-2">
                            <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noreferrer">Azure Portal</a> → App Registrations</li>
                            <li>Create a new app registration</li>
                            <li>Add API permissions: <code>Mail.Send</code></li>
                            <li>Create a client secret</li>
                            <li>Copy the Application (client) ID and Client Secret below</li>
                        </ol>
                    </Alert>

                    <Form onSubmit={handleSave}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Provider</Form.Label>
                                    <Form.Select value={formData.provider} onChange={(e) => setFormData({ ...formData, provider: e.target.value })}>
                                        <option value="outlook">Microsoft Outlook (OAuth)</option>
                                        <option value="gmail">Gmail (OAuth)</option>
                                        <option value="smtp">Custom SMTP</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email Address</Form.Label>
                                    <Form.Control type="email" value={formData.email_address}
                                        onChange={(e) => setFormData({ ...formData, email_address: e.target.value })}
                                        placeholder="noreply@yourcompany.com" />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Client ID (Application ID)</Form.Label>
                                    <Form.Control type="text" value={formData.client_id}
                                        onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Client Secret</Form.Label>
                                    <Form.Control type="password" value={formData.client_secret}
                                        onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                                        placeholder="Leave blank to keep existing" />
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr />
                        <h6>Notification Settings</h6>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Check type="switch" label="Send Low Stock Alerts" checked={formData.notify_low_stock}
                                        onChange={(e) => setFormData({ ...formData, notify_low_stock: e.target.checked })} />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Alert Frequency</Form.Label>
                                    <Form.Select value={formData.notify_frequency}
                                        onChange={(e) => setFormData({ ...formData, notify_frequency: e.target.value })}>
                                        <option value="immediate">Immediate</option>
                                        <option value="daily">Daily Summary</option>
                                        <option value="weekly">Weekly Summary</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Low Stock Threshold Override</Form.Label>
                                    <Form.Control type="number" value={formData.low_stock_threshold_override}
                                        onChange={(e) => setFormData({ ...formData, low_stock_threshold_override: e.target.value })}
                                        placeholder="Use item defaults" />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Notification Recipients (comma-separated emails)</Form.Label>
                            <Form.Control type="text" value={formData.notify_recipients}
                                onChange={(e) => setFormData({ ...formData, notify_recipients: e.target.value })}
                                placeholder="admin@company.com, manager@company.com" />
                        </Form.Group>

                        <div className="d-flex gap-2">
                            <Button type="submit" variant="primary" disabled={saving}>
                                {saving ? 'Saving...' : 'Save Configuration'}
                            </Button>
                            <Button variant="outline-primary" onClick={handleTest} disabled={testing || !config?.is_active}>
                                <Send className="me-2" />{testing ? 'Sending...' : 'Send Test Email'}
                            </Button>
                            <Button variant="outline-warning" onClick={handleSendLowStockAlert} disabled={!config?.is_active}>
                                Send Low Stock Alert Now
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            {/* Email Log */}
            <Card>
                <Card.Header><h5 className="mb-0">Recent Email Log</h5></Card.Header>
                <Card.Body className="p-0">
                    {emailLog.length === 0 ? (
                        <div className="text-center text-muted py-4">No emails sent yet</div>
                    ) : (
                        <Table striped hover size="sm" className="mb-0">
                            <thead><tr><th>Date</th><th>Type</th><th>Recipient</th><th>Subject</th><th>Status</th></tr></thead>
                            <tbody>
                                {emailLog.map((log, i) => (
                                    <tr key={i}>
                                        <td>{new Date(log.created_at).toLocaleString()}</td>
                                        <td><Badge bg="info">{log.template_type}</Badge></td>
                                        <td>{log.recipient}</td>
                                        <td>{log.subject}</td>
                                        <td><Badge bg={log.status === 'sent' ? 'success' : 'danger'}>{log.status}</Badge></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}

export default EmailSettings;
